import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { useAccountRestrictions } from '@/shared/accessControl'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { SlideOver } from '@/shared/ui/SlideOver'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { Modal } from '@/shared/ui/Modal'
import { Badge } from '@/shared/ui/Badge'
import { EmptyState } from '@/shared/ui/EmptyState'
import { toast } from '@/store/useToastStore'
import {
  Plus, Edit, Trash2, Loader2, Info, ImagePlus, ChevronDown, X as XIcon,
  Globe, Building2, Map, MessagesSquare, Clock, SlidersHorizontal, FileText, Inbox, Repeat,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  useCities, useCreateUpdateCity, useDeleteCity,
  useStates, useCreateUpdateState, useDeleteState,
  useDialects, useCreateUpdateDialect, useDeleteDialect,
  usePickups, useCreateUpdatePickup, useDeletePickup,
  useLanguages, useUploadFlagImage, useCreateLanguage, useUpdateLanguage, useDeleteLanguage,
  useAppSettings, useUpdateAppSettings,
  useTermsConditions, useUpdateTermsConditions,
  useDynamicServices, useCreateUpdateDynamicService, useDeleteDynamicService,
  useCategories, useSchools,
  useAutoReassignSettings, useUpdateAutoReassignSettings,
} from '@/api/hooks'
import { cn } from '@/shared/utils'
import { getApiError } from '@/api/client'

type TabColor = 'blue' | 'green' | 'amber' | 'purple'
interface TabMeta { label: string; path: string; key: string; icon: LucideIcon; color: TabColor; description: string }

const TABS: TabMeta[] = [
  { label:'Languages',       path:'/dashboard/system/language',        key:'language',
    icon: Globe, color:'blue', description: 'Languages available across the app, each with a flag and culture code.' },
  { label:'Cities',          path:'/dashboard/system/city',            key:'city',
    icon: Building2, color:'amber', description: 'Per-city pricing, certificate and reschedule-policy settings.' },
  { label:'States',          path:'/dashboard/system/state',           key:'state',
    icon: Map, color:'green', description: 'Areas/states within each city, in the order they should appear.' },
  { label:'Dialects',        path:'/dashboard/system/dialect',         key:'dialect',
    icon: MessagesSquare, color:'purple', description: 'Dialect entries used for language matching and search.' },
  { label:'Pickup Time Restriction', path:'/dashboard/system/pickup',  key:'pickup',
    icon: Clock, color:'blue', description: 'Date/time windows during which pickup is unavailable for a city.' },
  { label:'App Settings',    path:'/dashboard/system/app-settings',    key:'settings',
    icon: SlidersHorizontal, color:'amber', description: 'Fees, commissions, feature toggles and other app-wide configuration.' },
  { label:'Terms',           path:'/dashboard/system/terms-conditions',key:'terms',
    icon: FileText, color:'green', description: 'Terms & conditions sections, managed per language.' },
  { label:'Dynamic Services',path:'/dashboard/system/dynamic-services',key:'dynamic-services',
    icon: Inbox, color:'purple', description: 'Service types offered to students — fees, city, icon and active status.' },
  { label:'Auto-Reassign',  path:'/dashboard/system/auto-reassign',   key:'auto-reassign',
    icon: Repeat, color:'blue', description: 'How an unaccepted booking automatically finds a new trainer.' },
]

const HEADER_BAR: Record<TabColor, string> = {
  blue: 'bg-brand-500', green: 'bg-green-500', amber: 'bg-brand-500', purple: 'bg-purple-500',
}

// Colored section header reused above every list tab's table (icon + label + live count).
function SectionHeader({ tab, count, action }: { tab: TabMeta; count: number; action?: ReactNode }) {
  const Icon = tab.icon
  return (
    <div className={`px-5 py-3 flex items-center justify-between gap-3 ${HEADER_BAR[tab.color]}`}>
      <span className="flex items-center gap-2 text-sm font-bold text-white"><Icon size={15}/>{tab.label}</span>
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-white/80">{count} result{count !== 1 ? 's' : ''}</span>
        {action}
      </div>
    </div>
  )
}

const enName = (arr: any[]) => arr?.find((t: any) => t?.locale?.toLowerCase() === 'en')?.name ?? arr?.[0]?.name

// `start_date`/`end_date` come back as full ISO datetimes with a meaningless
// T00:00:00 time component — the real time of day lives in the separate
// `start_time`/`end_time` fields (e.g. "06:30:00"). Date and time are shown as
// their own columns, so each is formatted on its own rather than combined.
function fmtPickupDate(dateStr?: string | null) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return isNaN(d.getTime())
    ? dateStr.slice(0, 10)
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtPickupTime(timeStr?: string | null) {
  if (!timeStr) return '—'
  const [h, m] = timeStr.split(':').map(Number)
  return Number.isFinite(h) && Number.isFinite(m)
    ? new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })
    : timeStr
}

// ── Small shared form primitives (match the app's existing SlideOver-form style) ──
function Field({ label, value, onChange, placeholder, type='text', required, dir }: {
  label: string; value: string | number; onChange: (v: string) => void
  placeholder?: string; type?: string; required?: boolean; dir?: 'rtl' | 'ltr'
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">{label}{required && ' *'}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} dir={dir}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"/>
    </div>
  )
}
function SelectField({ label, value, onChange, options, required }: {
  label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }>; required?: boolean
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">{label}{required && ' *'}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white">
        <option value="">Select…</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}
function SwitchField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between py-1">
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</span>
      <button type="button" onClick={() => onChange(!checked)}
        className={cn('w-10 h-5 rounded-full relative transition-colors flex-shrink-0', checked ? 'bg-[#f68b2c]' : 'bg-gray-300')}>
        <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all', checked ? 'left-5' : 'left-0.5')}/>
      </button>
    </label>
  )
}
// Small uppercase divider used to group related fields inside a long form.
function FormSection({ label }: { label: string }) {
  return <div className="text-[13px] font-bold text-brand-600 uppercase tracking-wider border-t border-gray-100 pt-3 -mb-1">{label}</div>
}
function PublishedBadge({ published }: { published: boolean }) {
  return <Badge color={published ? 'green' : 'gray'}>{published ? 'Published' : 'Unpublished'}</Badge>
}
function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="flex gap-1">
      <button onClick={onEdit} className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-50 text-brand-600 hover:bg-brand-100"><Edit size={13}/></button>
      <button onClick={onDelete} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100"><Trash2 size={13}/></button>
    </div>
  )
}

const FALLBACK_LOCALES = [{ value: 'en', label: 'English' }, { value: 'ar', label: 'Arabic' }]

// Locale dropdown options are sourced from the Languages tab's own configured languages,
// same as the old app — falls back to en/ar if none are configured yet.
function useLocaleOptions() {
  const { data } = useLanguages({ limit: 100 })
  return useMemo(() => {
    const rows: any[] = data?.data ?? []
    const fromApi = rows.filter(l => l.language_culture).map(l => ({ value: l.language_culture, label: l.name ?? l.language_culture }))
    return fromApi.length ? fromApi : FALLBACK_LOCALES
  }, [data])
}

export function SystemPage() {
  const location = useLocation()
  const { canSeeAppSettings } = useAccountRestrictions()
  // Restricted accounts don't get the App Settings tab, and land on the first
  // allowed tab if they hit the URL directly.
  const visibleTabs = canSeeAppSettings ? TABS : TABS.filter(t => t.key !== 'settings')
  const matched = TABS.find(t => location.pathname.includes(t.key))
  const activeTab = (matched && (canSeeAppSettings || matched.key !== 'settings')) ? matched : visibleTabs[0]
  const tab = activeTab.key

  return (
    <div className="space-y-5">
      <PageHeader title="System Settings" subtitle={activeTab.description}
        breadcrumbs={[{label:'Dashboard',to:'/dashboard'},{label:'System Settings'}]}/>

      {tab === 'language' ? <LanguagesTab/>
        : tab === 'city' ? <CitiesTab/>
        : tab === 'state' ? <StatesTab/>
        : tab === 'dialect' ? <DialectsTab/>
        : tab === 'pickup' ? <PickupTab/>
        : tab === 'settings' ? <AppSettingsTab/>
        : tab === 'dynamic-services' ? <DynamicServicesTab/>
        : tab === 'auto-reassign' ? <AutoReassignTab/>
        : <TermsTab/>}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// CITIES — real payload is city_id + city_translation[] + pricing/certificate fields
// ══════════════════════════════════════════════════════════════════════════
function CitiesTab() {
  const { data, isLoading } = useCities({ limit: 200 })
  const save = useCreateUpdateCity()
  const del  = useDeleteCity()
  const localeOptions = useLocaleOptions()
  const rows: any[] = data?.data ?? []

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [toDelete, setToDelete] = useState<any | null>(null)
  const [confirmSave, setConfirmSave] = useState(false)
  const blank = {
    locale: 'en', name: '', is_published: true, is_certificate_available: false,
    show_to_trainer: true, show_to_student: true, show_to_school: true,
    certificate_price: '', certificate_link: '', reschedule_fee: '', trainer_change_fee: '',
    pickup_fee: '', max_slot: '', price_per_km: '', min_price: '',
    free_reschedule_before: '', free_reschedule_before_type: '0',
  }
  const [form, setForm] = useState(blank)
  const set = (k: keyof typeof blank) => (v: string) => setForm(f => ({ ...f, [k]: v }))

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true) }
  const openEdit = (c: any) => {
    setEditing(c)
    const tr = c.city_translations?.find((t: any) => t.locale?.toLowerCase() === blank.locale) ?? c.city_translations?.[0]
    setForm({
      locale: tr?.locale ?? 'en', name: tr?.name ?? '',
      is_published: c.is_published === 1 || c.is_published === true,
      is_certificate_available: !!c.is_certificate_available,
      show_to_trainer: c.show_to_trainer === 1 || c.show_to_trainer === true,
      show_to_student: c.show_to_student === 1 || c.show_to_student === true,
      show_to_school: c.show_to_school === 1 || c.show_to_school === true,
      certificate_price: c.certificate_price ?? '', certificate_link: c.certificate_link ?? '',
      reschedule_fee: c.reschedule_fee ?? '', trainer_change_fee: c.trainer_change_fee ?? '',
      pickup_fee: c.pickup_fee ?? '', max_slot: c.max_slot ?? '',
      price_per_km: c.price_per_km ?? '', min_price: c.min_price ?? '',
      free_reschedule_before: c.free_reschedule_before ?? '', free_reschedule_before_type: String(c.free_reschedule_before_type ?? 0),
    })
    setOpen(true)
  }
  const onLocaleChange = (locale: string) => {
    const tr = editing?.city_translations?.find((t: any) => t.locale === locale)
    setForm(f => ({ ...f, locale, name: tr?.name ?? '' }))
  }

  const handleSave = async () => {
    if (!form.name.trim() || !form.locale) { toast.error('Name and locale are required.'); return }
    try {
      await save.mutateAsync({
        city_id: editing?.id, locale: form.locale, name: form.name,
        is_published: form.is_published, is_certificate_available: form.is_certificate_available,
        show_to_trainer: form.show_to_trainer, show_to_student: form.show_to_student, show_to_school: form.show_to_school,
        certificate_price: form.certificate_price ? Number(form.certificate_price) : undefined,
        certificate_link: form.certificate_link || undefined,
        reschedule_fee: form.reschedule_fee ? Number(form.reschedule_fee) : undefined,
        trainer_change_fee: form.trainer_change_fee ? Number(form.trainer_change_fee) : undefined,
        pickup_fee: form.pickup_fee ? Number(form.pickup_fee) : undefined,
        max_slot: form.max_slot ? Number(form.max_slot) : undefined,
        price_per_km: form.price_per_km ? Number(form.price_per_km) : undefined,
        min_price: form.min_price ? Number(form.min_price) : undefined,
        free_reschedule_before: form.free_reschedule_before ? Number(form.free_reschedule_before) : undefined,
        free_reschedule_before_type: Number(form.free_reschedule_before_type) as 0 | 1,
      })
      toast.success(editing ? 'City updated successfully.' : 'City created successfully.')
      setOpen(false)
    } catch (e) { toast.error(getApiError(e)) } finally { setConfirmSave(false) }
  }

  const meta = TABS.find(t => t.key === 'city')!

  return (
    <>
      <Card padding={false} className="rounded-2xl overflow-hidden">
        <SectionHeader tab={meta} count={rows.length}
          action={<Button variant="outline" icon={<Plus size={14}/>} size="sm" onClick={openNew}>Add New</Button>}/>
        {isLoading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
          : rows.length === 0 ? <EmptyState message="No cities found" icon={<Building2 size={22}/>}/> : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>Name</th><th>Price/KM</th><th>Min Price</th><th>Certificate</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map((c: any) => (
                <tr key={c.id}>
                  <td className="font-medium text-gray-900">{enName(c.city_translations) ?? '—'}</td>
                  <td className="text-gray-600">{c.price_per_km ?? '—'}</td>
                  <td className="text-gray-600">{c.min_price ?? '—'}</td>
                  <td className="text-gray-600">{c.is_certificate_available ? `Yes (${c.certificate_price ?? 0})` : 'No'}</td>
                  <td><PublishedBadge published={c.is_published === 1 || c.is_published === true}/></td>
                  <td><RowActions onEdit={() => openEdit(c)} onDelete={() => setToDelete(c)}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <SlideOver open={open} onClose={() => setOpen(false)} title={editing ? 'Edit City' : 'Add New City'} width="max-w-xl">
        <div className="space-y-4">
          <p className="text-xs text-gray-400 -mt-1">Pricing, certificate and reschedule settings for this city.</p>

          <FormSection label="Translation"/>
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Locale" value={form.locale} onChange={onLocaleChange} options={localeOptions} required/>
            <Field label="City Name" value={form.name} onChange={set('name')} required dir={form.locale === 'ar' ? 'rtl' : 'ltr'}/>
          </div>

          <FormSection label="Publishing & Certificate"/>
          <SwitchField label="Published" checked={form.is_published} onChange={v => setForm(f => ({ ...f, is_published: v }))}/>
          <SwitchField label="Show to Trainer" checked={form.show_to_trainer} onChange={v => setForm(f => ({ ...f, show_to_trainer: v }))}/>
          <SwitchField label="Show to Student" checked={form.show_to_student} onChange={v => setForm(f => ({ ...f, show_to_student: v }))}/>
          <SwitchField label="Show to School" checked={form.show_to_school} onChange={v => setForm(f => ({ ...f, show_to_school: v }))}/>
          <SwitchField label="Certificate Available" checked={form.is_certificate_available} onChange={v => setForm(f => ({ ...f, is_certificate_available: v }))}/>
          {form.is_certificate_available && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Certificate Price" type="number" value={form.certificate_price} onChange={set('certificate_price')}/>
              <Field label="Certificate Link" type="url" value={form.certificate_link} onChange={set('certificate_link')}/>
            </div>
          )}

          <FormSection label="Pricing & Fees"/>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price Per KM" type="number" value={form.price_per_km} onChange={set('price_per_km')}/>
            <Field label="Min. Price" type="number" value={form.min_price} onChange={set('min_price')}/>
            <Field label="Reschedule Fee" type="number" value={form.reschedule_fee} onChange={set('reschedule_fee')}/>
            <Field label="Trainer Change Fee" type="number" value={form.trainer_change_fee} onChange={set('trainer_change_fee')}/>
            <Field label="Pickup Fee" type="number" value={form.pickup_fee} onChange={set('pickup_fee')}/>
            <Field label="Max Slot" type="number" value={form.max_slot} onChange={set('max_slot')}/>
          </div>

          <FormSection label="Reschedule Policy"/>
          <div className="grid grid-cols-2 gap-3 items-end">
            <SelectField label="Free Reschedule Before Type" value={form.free_reschedule_before_type} onChange={set('free_reschedule_before_type')}
              options={[{ value: '0', label: 'Hour' }, { value: '1', label: 'Day' }]}/>
            <Field label="Free Reschedule Before" type="number" value={form.free_reschedule_before} onChange={set('free_reschedule_before')}/>
          </div>
          <Button className="w-full justify-center" loading={save.isPending} onClick={() => setConfirmSave(true)}>Save</Button>
        </div>
      </SlideOver>

      <ConfirmDialog open={confirmSave} onClose={() => setConfirmSave(false)} onConfirm={handleSave}
        title={editing ? 'Save changes to this city?' : 'Create this city?'}
        description={editing ? `This updates pricing/certificate settings for "${form.name || 'this city'}".` : `"${form.name || 'This city'}" will be created.`}
        variant="primary" confirmLabel="Save" loading={save.isPending}/>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) { try { await del.mutateAsync(toDelete.id); toast.success('City deleted.') } catch (e) { toast.error(getApiError(e)) } } setToDelete(null) }}
        title="Delete city?" description={`This will remove "${toDelete ? enName(toDelete.city_translations) : ''}". This cannot be undone.`} loading={del.isPending}/>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// STATES — scoped to a city, FormData payload with translations[0][locale/name] + order
// ══════════════════════════════════════════════════════════════════════════
function StatesTab() {
  const { data, isLoading } = useStates({ limit: 200 })
  const { data: citiesData } = useCities({ limit: 500 })
  const save = useCreateUpdateState()
  const del  = useDeleteState()
  const localeOptions = useLocaleOptions()
  const rows: any[] = data?.data ?? []
  const cityOptions = (citiesData?.data ?? []).map((c: any) => ({ value: String(c.id), label: enName(c.city_translations) ?? `City #${c.id}` }))

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [toDelete, setToDelete] = useState<any | null>(null)
  const [confirmSave, setConfirmSave] = useState(false)
  const blank = { city_id: '', locale: 'en', name: '', order: '', is_published: true }
  const [form, setForm] = useState(blank)
  const set = (k: keyof typeof blank) => (v: string) => setForm(f => ({ ...f, [k]: v }))

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true) }
  const openEdit = (s: any) => {
    setEditing(s)
    const tr = s.translations?.find((t: any) => t.locale?.toLowerCase() === 'en') ?? s.translations?.[0]
    setForm({
      city_id: String(s.city_id ?? s.city?.id ?? ''), locale: tr?.locale ?? 'en', name: tr?.name ?? '',
      order: String(s.order ?? ''), is_published: s.is_published === 1 || s.is_published === '1' || s.is_published === true,
    })
    setOpen(true)
  }
  const onLocaleChange = (locale: string) => {
    const tr = editing?.translations?.find((t: any) => t.locale === locale)
    setForm(f => ({ ...f, locale, name: tr?.name ?? '' }))
  }

  const handleSave = async () => {
    if (!form.city_id || !form.name.trim() || form.order === '') { toast.error('City, name and order are required.'); return }
    try {
      await save.mutateAsync({
        state_id: editing?.id, city_id: Number(form.city_id), locale: form.locale, name: form.name,
        order: Number(form.order), is_published: form.is_published,
      })
      toast.success(editing ? 'State updated.' : 'State created.')
      setOpen(false)
    } catch (e) { toast.error(getApiError(e)) } finally { setConfirmSave(false) }
  }

  const meta = TABS.find(t => t.key === 'state')!

  return (
    <>
      <Card padding={false} className="rounded-2xl overflow-hidden">
        <SectionHeader tab={meta} count={rows.length}
          action={<Button variant="outline" icon={<Plus size={14}/>} size="sm" onClick={openNew}>Add New</Button>}/>
        {isLoading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
          : rows.length === 0 ? <EmptyState message="No states found" icon={<Map size={22}/>}/> : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>Name</th><th>City</th><th>Order</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map((s: any) => (
                <tr key={s.id}>
                  <td className="font-medium text-gray-900">{enName(s.translations) ?? '—'}</td>
                  <td className="text-gray-600">{enName(s.city?.city_translations) ?? '—'}</td>
                  <td className="text-gray-600">{s.order ?? '—'}</td>
                  <td><PublishedBadge published={s.is_published === 1 || s.is_published === '1' || s.is_published === true}/></td>
                  <td><RowActions onEdit={() => openEdit(s)} onDelete={() => setToDelete(s)}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <SlideOver open={open} onClose={() => setOpen(false)} title={editing ? 'Edit State' : 'Add New State'}>
        <div className="space-y-4">
          <SelectField label="City" value={form.city_id} onChange={set('city_id')} options={cityOptions} required/>
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Locale" value={form.locale} onChange={onLocaleChange} options={localeOptions} required/>
            <Field label="Name" value={form.name} onChange={set('name')} required dir={form.locale === 'ar' ? 'rtl' : 'ltr'}/>
          </div>
          <Field label="Order" type="number" value={form.order} onChange={set('order')} required/>
          <SwitchField label="Published" checked={form.is_published} onChange={v => setForm(f => ({ ...f, is_published: v }))}/>
          <Button className="w-full justify-center" loading={save.isPending} onClick={() => setConfirmSave(true)}>Save</Button>
        </div>
      </SlideOver>

      <ConfirmDialog open={confirmSave} onClose={() => setConfirmSave(false)} onConfirm={handleSave}
        title={editing ? 'Save changes to this state?' : 'Create this state?'} description={`"${form.name || 'This state'}" will be ${editing ? 'updated' : 'created'}.`}
        variant="primary" confirmLabel="Save" loading={save.isPending}/>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) { try { await del.mutateAsync(toDelete.id); toast.success('State deleted.') } catch (e) { toast.error(getApiError(e)) } } setToDelete(null) }}
        title="Delete state?" description={`This will remove "${toDelete ? enName(toDelete.translations) : ''}". This cannot be undone.`} loading={del.isPending}/>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// DIALECTS — language_name/dialect_name/keywords/description/order, not name/name_ar
// ══════════════════════════════════════════════════════════════════════════
function DialectsTab() {
  const { data, isLoading } = useDialects({ limit: 200 })
  const save = useCreateUpdateDialect()
  const del  = useDeleteDialect()
  const rows: any[] = data?.data ?? []

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [toDelete, setToDelete] = useState<any | null>(null)
  const [confirmSave, setConfirmSave] = useState(false)
  const blank = { language_name: '', dialect_name: '', keywords: '', description: '', order: '', is_published: true }
  const [form, setForm] = useState(blank)
  const set = (k: keyof typeof blank) => (v: string) => setForm(f => ({ ...f, [k]: v }))

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true) }
  const openEdit = (d: any) => {
    setEditing(d)
    setForm({
      language_name: d.language_name ?? '', dialect_name: d.dialect_name ?? '', keywords: d.keywords ?? '',
      description: d.description ?? '', order: String(d.order ?? ''), is_published: d.is_published === 1 || d.is_published === true,
    })
    setOpen(true)
  }

  const handleSave = async () => {
    if (!form.language_name.trim()) { toast.error('Language name is required.'); return }
    try {
      await save.mutateAsync({
        dialect_id: editing?.id, language_name: form.language_name, dialect_name: form.dialect_name,
        keywords: form.keywords, description: form.description, order: form.order ? Number(form.order) : undefined,
        is_published: form.is_published,
      })
      toast.success(editing ? 'Dialect updated.' : 'Dialect created.')
      setOpen(false)
    } catch (e) { toast.error(getApiError(e)) } finally { setConfirmSave(false) }
  }

  const meta = TABS.find(t => t.key === 'dialect')!

  return (
    <>
      <Card padding={false} className="rounded-2xl overflow-hidden">
        <SectionHeader tab={meta} count={rows.length}
          action={<Button variant="outline" icon={<Plus size={14}/>} size="sm" onClick={openNew}>Add New</Button>}/>
        {isLoading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
          : rows.length === 0 ? <EmptyState message="No dialects found" icon={<MessagesSquare size={22}/>}/> : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>Language</th><th>Dialect</th><th>Keywords</th><th>Order</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map((d: any) => (
                <tr key={d.id}>
                  <td className="font-medium text-gray-900">{d.language_name}</td>
                  <td className="text-gray-600">{d.dialect_name || '—'}</td>
                  <td className="text-gray-600 max-w-xs truncate">{d.keywords || '—'}</td>
                  <td className="text-gray-600">{d.order ?? '—'}</td>
                  <td><PublishedBadge published={d.is_published === 1 || d.is_published === true}/></td>
                  <td><RowActions onEdit={() => openEdit(d)} onDelete={() => setToDelete(d)}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <SlideOver open={open} onClose={() => setOpen(false)} title={editing ? 'Edit Dialect' : 'Add New Dialect'}>
        <div className="space-y-4">
          <Field label="Language Name" value={form.language_name} onChange={set('language_name')} required/>
          <Field label="Dialect Name" value={form.dialect_name} onChange={set('dialect_name')}/>
          <Field label="Keywords" value={form.keywords} onChange={set('keywords')}/>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"/>
          </div>
          <Field label="Order" type="number" value={form.order} onChange={set('order')}/>
          <SwitchField label="Published" checked={form.is_published} onChange={v => setForm(f => ({ ...f, is_published: v }))}/>
          <Button className="w-full justify-center" loading={save.isPending} onClick={() => setConfirmSave(true)}>Save</Button>
        </div>
      </SlideOver>

      <ConfirmDialog open={confirmSave} onClose={() => setConfirmSave(false)} onConfirm={handleSave}
        title={editing ? 'Save changes to this dialect?' : 'Create this dialect?'} description={`"${form.language_name || 'This dialect'}" will be ${editing ? 'updated' : 'created'}.`}
        variant="primary" confirmLabel="Save" loading={save.isPending}/>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) { try { await del.mutateAsync(toDelete.id); toast.success('Dialect deleted.') } catch (e) { toast.error(getApiError(e)) } } setToDelete(null) }}
        title="Delete dialect?" description={`This will remove "${toDelete?.language_name ?? ''}". This cannot be undone.`} loading={del.isPending}/>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// PICKUP TIME RESTRICTION — a city-scoped date/time exclusion window, not name/name_ar
// ══════════════════════════════════════════════════════════════════════════
function PickupTab() {
  const { data, isLoading } = usePickups({ limit: 200 })
  const { data: citiesData } = useCities({ limit: 500 })
  const save = useCreateUpdatePickup()
  const del  = useDeletePickup()
  const rows: any[] = data?.data ?? []
  const cityOptions = (citiesData?.data ?? []).map((c: any) => ({ value: String(c.id), label: enName(c.city_translations) ?? `City #${c.id}` }))

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [toDelete, setToDelete] = useState<any | null>(null)
  const [confirmSave, setConfirmSave] = useState(false)
  const blank = { city_id: '', start_date: '', end_date: '', start_time: '', end_time: '', status: true }
  const [form, setForm] = useState(blank)
  const set = (k: keyof typeof blank) => (v: string) => setForm(f => ({ ...f, [k]: v }))

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true) }
  const openEdit = (p: any) => {
    setEditing(p)
    setForm({
      city_id: String(p.city_id ?? p.city?.id ?? ''),
      start_date: (p.start_date ?? '').slice(0, 10), end_date: (p.end_date ?? '').slice(0, 10),
      start_time: (p.start_time ?? '').slice(0, 5), end_time: (p.end_time ?? '').slice(0, 5),
      status: p.status === 1 || p.status === true,
    })
    setOpen(true)
  }

  const handleSave = async () => {
    if (!form.city_id || !form.start_date || !form.end_date || !form.start_time || !form.end_time) { toast.error('City, dates and times are all required.'); return }
    try {
      await save.mutateAsync({
        id: editing?.id, city_id: Number(form.city_id), start_date: form.start_date, end_date: form.end_date,
        start_time: form.start_time, end_time: form.end_time, status: form.status,
      })
      toast.success(editing ? 'Pickup time restriction updated.' : 'Pickup time restriction created.')
      setOpen(false)
    } catch (e) { toast.error(getApiError(e)) } finally { setConfirmSave(false) }
  }

  const meta = TABS.find(t => t.key === 'pickup')!

  return (
    <>
      <Card padding={false} className="rounded-2xl overflow-hidden">
        <SectionHeader tab={meta} count={rows.length}
          action={<Button variant="outline" icon={<Plus size={14}/>} size="sm" onClick={openNew}>Add New</Button>}/>
        {isLoading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
          : rows.length === 0 ? <EmptyState message="No pickup time restrictions found" icon={<Clock size={22}/>}/> : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr>
              <th>City</th><th>Date</th><th>Time</th><th>Status</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((p: any) => (
                <tr key={p.id}>
                  <td className="font-medium text-gray-900">{enName(p.city?.city_translations) ?? '—'}</td>
                  <td className="text-gray-600">{fmtPickupDate(p.start_date)} – {fmtPickupDate(p.end_date)}</td>
                  <td className="text-gray-600">{fmtPickupTime(p.start_time)} – {fmtPickupTime(p.end_time)}</td>
                  <td><PublishedBadge published={p.status === 1 || p.status === true}/></td>
                  <td><RowActions onEdit={() => openEdit(p)} onDelete={() => setToDelete(p)}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <SlideOver open={open} onClose={() => setOpen(false)} title={editing ? 'Edit Pickup Time Restriction' : 'Add New Pickup Time Restriction'}>
        <div className="space-y-4">
          <SelectField label="City" value={form.city_id} onChange={set('city_id')} options={cityOptions} required/>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start Date" type="date" value={form.start_date} onChange={set('start_date')} required/>
            <Field label="Start Time" type="time" value={form.start_time} onChange={set('start_time')} required/>
            <Field label="End Date" type="date" value={form.end_date} onChange={set('end_date')} required/>
            <Field label="End Time" type="time" value={form.end_time} onChange={set('end_time')} required/>
          </div>
          <SwitchField label="Active" checked={form.status} onChange={v => setForm(f => ({ ...f, status: v }))}/>
          <Button className="w-full justify-center" loading={save.isPending} onClick={() => setConfirmSave(true)}>Save</Button>
        </div>
      </SlideOver>

      <ConfirmDialog open={confirmSave} onClose={() => setConfirmSave(false)} onConfirm={handleSave}
        title={editing ? 'Save changes to this pickup time restriction?' : 'Create this pickup time restriction?'} description={`This window will be ${editing ? 'updated' : 'created'} for the selected city.`}
        variant="primary" confirmLabel="Save" loading={save.isPending}/>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) { try { await del.mutateAsync(toDelete.id); toast.success('Pickup time restriction deleted.') } catch (e) { toast.error(getApiError(e)) } } setToDelete(null) }}
        title="Delete pickup time restriction?" description="This cannot be undone." loading={del.isPending}/>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// LANGUAGES — needs an uploaded flag image (flag_id) + culture code + display order
// ══════════════════════════════════════════════════════════════════════════
function LanguagesTab() {
  const { data, isLoading } = useLanguages({ limit: 200 })
  const uploadFlag = useUploadFlagImage()
  const create = useCreateLanguage()
  const update = useUpdateLanguage()
  const del    = useDeleteLanguage()
  const rows: any[] = data?.data ?? []

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [toDelete, setToDelete] = useState<any | null>(null)
  const [confirmSave, setConfirmSave] = useState(false)
  const blank = { name: '', language_culture: '', display_order: '', published: true }
  const [form, setForm] = useState(blank)
  const [flagFile, setFlagFile] = useState<File | null>(null)
  const [flagPreview, setFlagPreview] = useState<string>('')
  const set = (k: keyof typeof blank) => (v: string) => setForm(f => ({ ...f, [k]: v }))
  const saving = uploadFlag.isPending || create.isPending || update.isPending

  const openNew = () => { setEditing(null); setForm(blank); setFlagFile(null); setFlagPreview(''); setOpen(true) }
  const openEdit = (l: any) => {
    setEditing(l)
    setForm({ name: l.name ?? '', language_culture: l.language_culture ?? '', display_order: String(l.display_order ?? ''), published: l.published === 1 || l.published === true })
    setFlagFile(null); setFlagPreview(l.flag?.virtual_path ?? l.flag_url ?? '')
    setOpen(true)
  }
  const onFlagChange = (file: File | null) => {
    setFlagFile(file)
    setFlagPreview(file ? URL.createObjectURL(file) : '')
  }

  const handleSave = async () => {
    if (!form.name.trim() || !/^[a-zA-Z]{2}$/.test(form.language_culture) || !form.display_order) {
      toast.error('Name, a 2-letter language culture code, and display order are required.'); return
    }
    if (!flagFile && !editing?.flag_id) { toast.error('A flag image is required.'); return }
    try {
      let flag_id: number = editing?.flag_id
      if (flagFile) {
        const uploaded = await uploadFlag.mutateAsync(flagFile)
        flag_id = uploaded.data.id
      }
      const payload = { flag_id, language_culture: form.language_culture, name: form.name, display_order: Number(form.display_order), published: form.published }
      if (editing) await update.mutateAsync({ id: editing.id, ...payload })
      else await create.mutateAsync(payload)
      toast.success(editing ? 'Language updated.' : 'Language created.')
      setOpen(false)
    } catch (e) { toast.error(getApiError(e)) } finally { setConfirmSave(false) }
  }

  const meta = TABS.find(t => t.key === 'language')!

  return (
    <>
      <Card padding={false} className="rounded-2xl overflow-hidden">
        <SectionHeader tab={meta} count={rows.length}
          action={<Button variant="outline" icon={<Plus size={14}/>} size="sm" onClick={openNew}>Add New</Button>}/>
        {isLoading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
          : rows.length === 0 ? <EmptyState message="No languages found" icon={<Globe size={22}/>}/> : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>Flag</th><th>Name</th><th>Culture</th><th>Order</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map((l: any) => (
                <tr key={l.id}>
                  <td>{l.flag?.virtual_path ? <img src={l.flag.virtual_path} alt={l.name} className="w-6 h-4 object-cover rounded-sm border border-gray-100"/> : '—'}</td>
                  <td className="font-medium text-gray-900">{l.name}</td>
                  <td className="text-gray-600 uppercase">{l.language_culture}</td>
                  <td className="text-gray-600">{l.display_order ?? '—'}</td>
                  <td><PublishedBadge published={l.published === 1 || l.published === true}/></td>
                  <td><RowActions onEdit={() => openEdit(l)} onDelete={() => setToDelete(l)}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <SlideOver open={open} onClose={() => setOpen(false)} title={editing ? 'Edit Language' : 'Add New Language'}>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Flag Image {!editing && '*'}</label>
            <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-lg p-3 cursor-pointer hover:border-brand-400">
              {flagPreview ? <img src={flagPreview} alt="Flag" className="w-10 h-7 object-cover rounded-sm border border-gray-100"/> : <ImagePlus size={20} className="text-gray-400"/>}
              <span className="text-xs text-gray-500">{flagFile ? flagFile.name : 'Click to upload a flag image'}</span>
              <input type="file" accept="image/*" className="hidden" onChange={e => onFlagChange(e.target.files?.[0] ?? null)}/>
            </label>
          </div>
          <Field label="Name" value={form.name} onChange={set('name')} required/>
          <Field label="Language Culture (e.g. en, ar)" value={form.language_culture} onChange={v => set('language_culture')(v.slice(0, 2))} required/>
          <Field label="Display Order" type="number" value={form.display_order} onChange={set('display_order')} required/>
          <SwitchField label="Published" checked={form.published} onChange={v => setForm(f => ({ ...f, published: v }))}/>
          <Button className="w-full justify-center" loading={saving} onClick={() => setConfirmSave(true)}>Save</Button>
        </div>
      </SlideOver>

      <ConfirmDialog open={confirmSave} onClose={() => setConfirmSave(false)} onConfirm={handleSave}
        title={editing ? 'Save changes to this language?' : 'Create this language?'} description={`"${form.name || 'This language'}" will be ${editing ? 'updated' : 'created'}.`}
        variant="primary" confirmLabel="Save" loading={saving}/>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) { try { await del.mutateAsync(toDelete.id); toast.success('Language deleted.') } catch (e) { toast.error(getApiError(e)) } } setToDelete(null) }}
        title="Delete language?" description={`This will remove "${toDelete?.name ?? ''}". This cannot be undone.`} loading={del.isPending}/>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// APP SETTINGS — type-aware: boolean switch (auto-save), category/school picker,
// privacy-policy long text, everything else a text field — each with its own Save.
// ══════════════════════════════════════════════════════════════════════════
const FIELD_TOOLTIPS: Record<string, string> = {
  DEFAULT_MAX_CASH_IN_HAND_ALLOWED: 'The maximum amount of cash a trainer is allowed to hold for transactions.',
  REWARD_FEE: 'The commission deducted from the reward given to the trainer by the student.',
  TAX_RATE: 'The percentage of tax applied to the booking amount.',
  CANCELLATION_FEE: 'The fee charged when a student cancels a booking.',
  MINIMUM_KM: 'The minimum distance covered that determines the base price for the pickup option.',
  SLOT_DURATION: 'The duration of each booking slot.',
  CASH_FEE: 'Additional fee applied when the payment method is cash.',
  FREE_PICKUP_FEE_FOR_CHANGING_TRAINER: 'If a student changes trainers within the same school, the first few kilometers are free.',
}
const STRICT_BOOL_STRING_KEYS = ['STRIPE_ENABLED', 'CASH_ENABLED']

function prettyKey(key: string) {
  return key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
}

function AppSettingsTab() {
  const { data, isLoading } = useAppSettings({ limit: 1000, locale: 'en' })
  const update = useUpdateAppSettings()
  const { data: categoriesData } = useCategories({ limit: 500 })
  const { data: schoolsData } = useSchools({ limit: 500 })
  const rows: Array<{ id: number; key: string; value: any }> = data?.data ?? []
  const [edits, setEdits] = useState<Record<number, any>>({})
  const [savingId, setSavingId] = useState<number | null>(null)
  const [pendingChange, setPendingChange] = useState<{ row: { id: number; key: string }; value: any; isToggle?: boolean } | null>(null)

  const categoryOptions = (categoriesData?.data ?? []).map((c: any) => ({ value: String(c.id), label: enName(c.category_translations) ?? `#${c.id}` }))
  const schoolOptions = (schoolsData?.data ?? []).map((s: any) => ({ value: String(s.id), label: enName(s.vendor_translations) ?? `#${s.id}` }))

  // Short values first, long strings next, booleans last — matches the old app's grouping.
  const sorted = useMemo(() => {
    const short = rows.filter(r => (typeof r.value === 'number' || typeof r.value === 'string') && String(r.value).length <= 20)
    const long  = rows.filter(r => typeof r.value === 'string' && r.value.length > 20)
    const bools = rows.filter(r => typeof r.value === 'boolean')
    return [...short, ...long, ...bools]
  }, [rows])

  const valueOf = (r: { id: number; value: any }) => edits[r.id] ?? r.value
  const setVal = (id: number, v: any) => setEdits(x => ({ ...x, [id]: v }))

  const save = async (row: { id: number; key: string }, valueOverride?: any) => {
    const value = valueOverride !== undefined ? valueOverride : edits[row.id]
    if (STRICT_BOOL_STRING_KEYS.includes(row.key) && value !== 'true' && value !== 'false') {
      toast.error('Only "true" or "false" are allowed for this setting.'); return
    }
    setSavingId(row.id)
    try {
      await update.mutateAsync({ id: row.id, key: row.key, value: String(value) })
      toast.success('Setting saved.')
    } catch (e) { toast.error(getApiError(e)) } finally { setSavingId(null) }
  }

  const requestSave = (row: { id: number; key: string }, value: any, isToggle?: boolean) => setPendingChange({ row, value, isToggle })
  const confirmPendingChange = async () => {
    if (!pendingChange) return
    const { row, value } = pendingChange
    setVal(row.id, value)
    await save(row, value)
    setPendingChange(null)
  }

  if (isLoading) return <Card><div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-500" size={22}/></div></Card>
  if (rows.length === 0) return <Card><EmptyState message="No settings found" icon={<SlidersHorizontal size={22}/>}/></Card>

  const booleans = sorted.filter(r => typeof r.value === 'boolean')
  const longText  = sorted.filter(r => typeof r.value === 'string' && r.value.length > 20)
  const keyValues = sorted.filter(r => !booleans.includes(r) && !longText.includes(r))

  const renderRow = (row: { id: number; key: string; value: any }) => {
    const isPrivacyPolicy = row.key.toUpperCase().includes('PRIVACY POLICY') || row.key.toUpperCase().includes('PRIVACY_POLICY')
    const isDefaultCategory = row.key === 'DEFAULT_CATEGORY'
    const isDefaultSchool = row.key === 'DEFAULT_SCHOOL'
    const tooltip = FIELD_TOOLTIPS[row.key]

    return (
      <Card key={row.id} className="rounded-2xl">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-sm font-bold text-gray-900">{prettyKey(row.key)}</span>
          {tooltip && <span title={tooltip}><Info size={13} className="text-gray-400"/></span>}
        </div>

        {isDefaultCategory ? (
          <div className="flex gap-2">
            <SelectField label="" value={String(valueOf(row) ?? '')} onChange={v => setVal(row.id, v)} options={categoryOptions}/>
            <Button size="sm" loading={savingId === row.id} onClick={() => requestSave(row, valueOf(row))}>Save</Button>
          </div>
        ) : isDefaultSchool ? (
          <div className="flex gap-2">
            <SelectField label="" value={String(valueOf(row) ?? '')} onChange={v => setVal(row.id, v)} options={schoolOptions}/>
            <Button size="sm" loading={savingId === row.id} onClick={() => requestSave(row, valueOf(row))}>Save</Button>
          </div>
        ) : isPrivacyPolicy ? (
          <div className="space-y-2">
            <textarea value={valueOf(row) ?? ''} onChange={e => setVal(row.id, e.target.value)} rows={8}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 leading-relaxed"/>
            <Button size="sm" loading={savingId === row.id} onClick={() => requestSave(row, valueOf(row))}>Save</Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input value={valueOf(row) ?? ''} onChange={e => setVal(row.id, e.target.value)}
              placeholder={STRICT_BOOL_STRING_KEYS.includes(row.key) ? 'Enter "true" or "false"' : 'Enter value'}
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"/>
            <Button size="sm" loading={savingId === row.id} onClick={() => requestSave(row, valueOf(row))}>Save</Button>
          </div>
        )}
        {STRICT_BOOL_STRING_KEYS.includes(row.key) && <p className="text-xs text-gray-400 mt-1">Only &quot;true&quot; or &quot;false&quot; are allowed.</p>}
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog open={!!pendingChange} onClose={() => setPendingChange(null)} onConfirm={confirmPendingChange}
        title={pendingChange?.isToggle ? `${pendingChange.value ? 'Enable' : 'Disable'} "${prettyKey(pendingChange.row.key)}"?` : `Save "${pendingChange ? prettyKey(pendingChange.row.key) : ''}"?`}
        description={pendingChange?.isToggle
          ? `This will ${pendingChange.value ? 'turn on' : 'turn off'} this feature toggle app-wide.`
          : 'This app-wide setting will be updated immediately.'}
        variant="primary" confirmLabel={pendingChange?.isToggle ? (pendingChange.value ? 'Enable' : 'Disable') : 'Save'}
        loading={pendingChange ? savingId === pendingChange.row.id : false}/>

      {booleans.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Feature Toggles</h3>
          <Card padding={false} className="rounded-2xl divide-y divide-gray-50">
            {booleans.map(row => (
              <div key={row.id} className="px-5 py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-sm font-semibold text-gray-800 truncate">{prettyKey(row.key)}</span>
                  {FIELD_TOOLTIPS[row.key] && <span title={FIELD_TOOLTIPS[row.key]}><Info size={13} className="text-gray-400 flex-shrink-0"/></span>}
                </div>
                <button type="button" onClick={() => requestSave(row, !valueOf(row), true)}
                  className={cn('w-10 h-5 rounded-full relative transition-colors flex-shrink-0', valueOf(row) ? 'bg-[#f68b2c]' : 'bg-gray-300')}>
                  <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all', valueOf(row) ? 'left-5' : 'left-0.5')}/>
                </button>
              </div>
            ))}
          </Card>
        </div>
      )}

      {keyValues.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Key Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {keyValues.map(renderRow)}
          </div>
        </div>
      )}

      {longText.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Long-Form Content</h3>
          <div className="space-y-3">{longText.map(renderRow)}</div>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// DYNAMIC SERVICES — service TYPES offered to students (fees/city/icon/order),
// distinct from the registrations-approval page. Ported from the old app's
// system/dynamic-services screen, which this dashboard was missing entirely.
// ══════════════════════════════════════════════════════════════════════════
function DynamicServicesTab() {
  const [cityFilter, setCityFilter] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading } = useDynamicServices({ page, limit: 10, city_id: cityFilter || undefined })
  const { data: citiesData } = useCities({ limit: 500 })
  const save = useCreateUpdateDynamicService()
  const del  = useDeleteDynamicService()
  const rows: any[] = data?.data ?? []
  const total = data?.total ?? rows.length
  const perPage = data?.count_per_page ?? 10
  const lastPage = Math.max(1, Math.ceil(total / perPage) || 1)
  const cityOptions = (citiesData?.data ?? []).map((c: any) => ({ value: String(c.id), label: enName(c.city_translations) ?? `City #${c.id}` }))
  const cityName = (id: any) => cityOptions.find((o: any) => o.value === String(id))?.label ?? '—'

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [toDelete, setToDelete] = useState<any | null>(null)
  const [confirmSave, setConfirmSave] = useState(false)
  const blank = { name: '', name_ar: '', subtitle: '', subtitle_ar: '', fees: '', currency: 'AED', city_id: '', display_order: '', is_active: true }
  const [form, setForm] = useState(blank)
  const [iconFile, setIconFile] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState('')
  const set = (k: keyof typeof blank) => (v: string) => setForm(f => ({ ...f, [k]: v }))
  const saving = save.isPending

  const openNew = () => { setEditing(null); setForm(blank); setIconFile(null); setIconPreview(''); setOpen(true) }
  const openEdit = (s: any) => {
    setEditing(s)
    setForm({
      name: s.name ?? '', name_ar: s.name_ar ?? '', subtitle: s.subtitle ?? '', subtitle_ar: s.subtitle_ar ?? '',
      fees: String(s.fees ?? ''), currency: s.currency ?? 'AED', city_id: s.city_id != null ? String(s.city_id) : '',
      display_order: s.display_order != null ? String(s.display_order) : '', is_active: !!s.is_active,
    })
    setIconFile(null); setIconPreview(s.icon_url ?? '')
    setOpen(true)
  }
  const onIconChange = (file: File | null) => {
    setIconFile(file)
    setIconPreview(file ? URL.createObjectURL(file) : '')
  }

  const handleSave = async () => {
    if (!form.name.trim() || !form.name_ar.trim() || !form.subtitle.trim() || !form.subtitle_ar.trim() || form.fees === '' || !form.currency.trim() || !form.city_id) {
      toast.error('Name (EN/AR), subtitle (EN/AR), fees, currency and city are all required.'); return
    }
    try {
      await save.mutateAsync({
        id: editing?.id, name: form.name, name_ar: form.name_ar, subtitle: form.subtitle, subtitle_ar: form.subtitle_ar,
        fees: Number(form.fees), currency: form.currency, city_id: form.city_id,
        display_order: form.display_order !== '' ? Number(form.display_order) : undefined,
        is_active: form.is_active, icon: iconFile,
      })
      toast.success(editing ? 'Service updated.' : 'Service created.')
      setOpen(false)
    } catch (e) { toast.error(getApiError(e)) } finally { setConfirmSave(false) }
  }

  const meta = TABS.find(t => t.key === 'dynamic-services')!

  return (
    <>
      <Card padding={false} className="rounded-2xl overflow-hidden">
        <SectionHeader tab={meta} count={total}
          action={<Button variant="outline" icon={<Plus size={14}/>} size="sm" onClick={openNew}>Add New</Button>}/>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
          <select value={cityFilter} onChange={e => { setCityFilter(e.target.value); setPage(1) }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white min-w-48 focus:outline-none focus:ring-2 focus:ring-brand-400">
            <option value="">All Cities</option>
            {cityOptions.map((o: any) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {isLoading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
          : rows.length === 0 ? <EmptyState message="No dynamic services found" icon={<Inbox size={22}/>}/> : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>Service</th><th>Subtitle</th><th>Fees</th><th>City</th><th>Order</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map((s: any) => (
                <tr key={s.id}>
                  <td>
                    <div className="flex items-center gap-2.5">
                      {s.icon_url
                        ? <img src={s.icon_url} alt={s.name} className="w-8 h-8 object-contain rounded-lg border border-gray-100 bg-gray-50"/>
                        : <div className="w-8 h-8 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center"><Inbox size={14} className="text-gray-300"/></div>}
                      <div>
                        <div className="font-medium text-gray-900">{s.name}</div>
                        <div className="text-xs text-gray-400" dir="rtl">{s.name_ar}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-gray-600">{s.subtitle || '—'}</td>
                  <td className="text-gray-600 font-semibold">{s.currency} {s.fees}</td>
                  <td className="text-gray-600">{cityName(s.city_id)}</td>
                  <td className="text-gray-600">{s.display_order ?? '—'}</td>
                  <td><Badge color={s.is_active ? 'green' : 'gray'}>{s.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td><RowActions onEdit={() => openEdit(s)} onDelete={() => setToDelete(s)}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {!isLoading && rows.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-4 flex-wrap">
            <span className="text-xs text-gray-500">Page {page} of {lastPage} · {total} total</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">‹</button>
              <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">›</button>
            </div>
          </div>
        )}
      </Card>

      <SlideOver open={open} onClose={() => setOpen(false)} title={editing ? 'Edit Service' : 'Add New Service'} width="max-w-xl">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Icon</label>
            <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-lg p-3 cursor-pointer hover:border-brand-400">
              {iconPreview ? <img src={iconPreview} alt="Icon" className="w-10 h-10 object-contain rounded-lg border border-gray-100"/> : <ImagePlus size={20} className="text-gray-400"/>}
              <span className="text-xs text-gray-500">{iconFile ? iconFile.name : 'Click to upload an icon (PNG, SVG, JPG)'}</span>
              <input type="file" accept="image/*,.svg" className="hidden" onChange={e => onIconChange(e.target.files?.[0] ?? null)}/>
            </label>
          </div>

          <FormSection label="Details"/>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name (EN)" value={form.name} onChange={set('name')} required/>
            <Field label="Name (AR)" value={form.name_ar} onChange={set('name_ar')} required dir="rtl"/>
            <Field label="Subtitle (EN)" value={form.subtitle} onChange={set('subtitle')} required/>
            <Field label="Subtitle (AR)" value={form.subtitle_ar} onChange={set('subtitle_ar')} required dir="rtl"/>
          </div>

          <FormSection label="Pricing & Placement"/>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fees" type="number" value={form.fees} onChange={set('fees')} required/>
            <Field label="Currency" value={form.currency} onChange={set('currency')} required/>
            <SelectField label="City" value={form.city_id} onChange={set('city_id')} options={cityOptions} required/>
            <Field label="Display Order" type="number" value={form.display_order} onChange={set('display_order')} placeholder="Lower number = higher priority"/>
          </div>
          <SwitchField label="Active" checked={form.is_active} onChange={v => setForm(f => ({ ...f, is_active: v }))}/>
          <Button className="w-full justify-center" loading={saving} onClick={() => setConfirmSave(true)}>Save</Button>
        </div>
      </SlideOver>

      <ConfirmDialog open={confirmSave} onClose={() => setConfirmSave(false)} onConfirm={handleSave}
        title={editing ? 'Save changes to this service?' : 'Create this service?'} description={`"${form.name || 'This service'}" will be ${editing ? 'updated' : 'created'}.`}
        variant="primary" confirmLabel="Save" loading={saving}/>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) { try { await del.mutateAsync(toDelete.id); toast.success('Service deleted.') } catch (e) { toast.error(getApiError(e)) } } setToDelete(null) }}
        title="Delete service?" description={`This will remove "${toDelete?.name ?? ''}". This cannot be undone.`} loading={del.isPending}/>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// AUTO-REASSIGN — a single settings object (GET/POST the same endpoint), not
// a list: how an unaccepted booking automatically hunts for a new trainer.
// ══════════════════════════════════════════════════════════════════════════
const AUTO_REASSIGN_FALLBACK_MODES = ['student_choice', 'auto_assign']

function AutoReassignTab() {
  const { data, isLoading } = useAutoReassignSettings()
  const update = useUpdateAutoReassignSettings()
  const settings = data?.data

  // The API works in minutes (timeout_minutes), but hours read far easier for
  // an admin setting this (10080 minutes vs. 168 hours) — the form holds hours
  // and converts both ways at the seed/save boundary.
  const blank = { mode: '', enabled: true, timeout_hours: '', max_hops: '', max_distance_km: '' }
  const [form, setForm] = useState(blank)
  const [seeded, setSeeded] = useState(false)
  const [confirmSave, setConfirmSave] = useState(false)

  if (settings && !seeded) {
    setSeeded(true)
    setForm({
      mode: settings.mode ?? '',
      enabled: !!settings.enabled,
      timeout_hours: settings.timeout_minutes != null ? String(Math.round((settings.timeout_minutes / 60) * 100) / 100) : '',
      max_hops: String(settings.max_hops ?? ''),
      max_distance_km: String(settings.max_distance_km ?? ''),
    })
  }

  const modeOptions = (settings?.modes ?? AUTO_REASSIGN_FALLBACK_MODES).map((m: string) => ({ value: m, label: prettyKey(m) }))

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        mode: form.mode || undefined,
        enabled: form.enabled,
        timeout_minutes: form.timeout_hours ? Math.round(Number(form.timeout_hours) * 60) : undefined,
        max_hops: form.max_hops ? Number(form.max_hops) : undefined,
        max_distance_km: form.max_distance_km ? Number(form.max_distance_km) : undefined,
      })
      toast.success('Auto-reassign settings updated.')
    } catch (e) { toast.error(getApiError(e)) } finally { setConfirmSave(false) }
  }

  if (isLoading) return <Card><div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-500" size={22}/></div></Card>

  return (
    <>
      <Card className="rounded-2xl max-w-xl space-y-4">
        <p className="text-xs text-gray-400 -mt-1">Controls how an unaccepted booking automatically finds a replacement trainer.</p>

        <SwitchField label="Enabled" checked={form.enabled} onChange={v => setForm(f => ({ ...f, enabled: v }))}/>
        <SelectField label="Mode" value={form.mode} onChange={v => setForm(f => ({ ...f, mode: v }))} options={modeOptions} required/>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Timeout (hours)" type="number" value={form.timeout_hours}
            onChange={v => setForm(f => ({ ...f, timeout_hours: v }))} required/>
          <Field label="Max Hops" type="number" value={form.max_hops}
            onChange={v => setForm(f => ({ ...f, max_hops: v }))} required/>
          <Field label="Max Distance (km)" type="number" value={form.max_distance_km}
            onChange={v => setForm(f => ({ ...f, max_distance_km: v }))} required/>
        </div>

        <Button className="w-full justify-center" loading={update.isPending} onClick={() => setConfirmSave(true)}>Save</Button>
      </Card>

      <ConfirmDialog open={confirmSave} onClose={() => setConfirmSave(false)} onConfirm={handleSave}
        title="Save auto-reassign settings?"
        description="This changes how bookings automatically find a replacement trainer, app-wide."
        variant="primary" confirmLabel="Save" loading={update.isPending}/>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// TERMS & CONDITIONS — per-locale array of {heading, content} sections, not a flat string
// ══════════════════════════════════════════════════════════════════════════
function TermsTab() {
  const localeOptions = useLocaleOptions()
  const [locale, setLocale] = useState('en')
  useEffect(() => { if (!locale && localeOptions[0]) setLocale(localeOptions[0].value) }, [localeOptions, locale])

  const { data, isLoading } = useTermsConditions(locale)
  const update = useUpdateTermsConditions()
  const row = (data?.data ?? [])[0]
  const sections: Array<{ heading: string; content: string }> = row?.value ?? []
  const [expanded, setExpanded] = useState<number | null>(null)

  const [editOpen, setEditOpen] = useState(false)
  const [draft, setDraft] = useState<Array<{ heading: string; content: string }>>([])
  const [confirmSave, setConfirmSave] = useState(false)

  const openEditor = () => {
    setDraft(sections.length ? sections.map(s => ({ ...s })) : [{ heading: '', content: '' }])
    setEditOpen(true)
  }
  const updateDraft = (i: number, field: 'heading' | 'content', value: string) => {
    setDraft(d => d.map((s, idx) => idx === i ? { ...s, [field]: value } : s))
  }
  const addSection = () => setDraft(d => [...d, { heading: '', content: '' }])
  const removeSection = (i: number) => setDraft(d => d.length > 1 ? d.filter((_, idx) => idx !== i) : d)

  const handleSave = async () => {
    const cleaned = draft.filter(s => s.heading.trim() || s.content.trim())
    if (cleaned.length === 0) { toast.error('Add at least one section.'); return }
    try {
      await update.mutateAsync({ content: cleaned, locale, display_order: row?.display_order ?? 1 })
      toast.success('Terms & conditions saved.')
      setEditOpen(false)
    } catch (e) { toast.error(getApiError(e)) } finally { setConfirmSave(false) }
  }

  const meta = TABS.find(t => t.key === 'terms')!

  return (
    <>
      <Card padding={false} className="rounded-2xl overflow-hidden">
        <SectionHeader tab={meta} count={sections.length}
          action={
            <div className="flex items-center gap-2">
              <select value={locale} onChange={e => setLocale(e.target.value)}
                className="text-xs font-medium bg-white/90 text-gray-700 rounded-lg px-2 py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-white/50">
                {localeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={openEditor}>Edit Sections</Button>
            </div>
          }/>

        <div className="p-5">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-500" size={22}/></div>
          ) : sections.length === 0 ? (
            <EmptyState message="No terms & conditions for this locale yet" icon={<FileText size={22}/>}/>
          ) : (
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {sections.map((s, i) => (
                <div key={i}>
                  <button onClick={() => setExpanded(x => x === i ? null : i)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
                    <span className="w-6 h-6 rounded-full bg-green-50 text-green-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <span className="text-sm font-semibold text-gray-900 flex-1">{s.heading || `Section ${i + 1}`}</span>
                    <ChevronDown size={16} className={cn('text-gray-400 transition-transform flex-shrink-0', expanded === i && 'rotate-180')}/>
                  </button>
                  {expanded === i && <div className="px-4 pb-4 pl-9 text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{s.content}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title={`Edit Terms & Conditions (${localeOptions.find(o => o.value === locale)?.label ?? locale})`} size="lg">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {draft.map((s, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-3 relative">
              {draft.length > 1 && (
                <button onClick={() => removeSection(i)} className="absolute top-2 right-2 text-gray-300 hover:text-red-500"><XIcon size={14}/></button>
              )}
              <Field label="Heading" value={s.heading} onChange={v => updateDraft(i, 'heading', v)}/>
              <div className="mt-3">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Content</label>
                <textarea value={s.content} onChange={e => updateDraft(i, 'content', e.target.value)} rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"/>
              </div>
            </div>
          ))}
          <button onClick={addSection} className="text-sm font-semibold text-brand-600 hover:underline">+ Add Another Section</button>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button loading={update.isPending} onClick={() => setConfirmSave(true)}>Save</Button>
        </div>
      </Modal>

      <ConfirmDialog open={confirmSave} onClose={() => setConfirmSave(false)} onConfirm={handleSave}
        title="Save terms & conditions?" description={`This updates the published terms & conditions for "${localeOptions.find(o => o.value === locale)?.label ?? locale}".`}
        variant="primary" confirmLabel="Save" loading={update.isPending}/>
    </>
  )
}
